import {
  FC, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { ProspectOneProps } from 'utils/types';

import cancelSvg from '/prospectCancel.svg';

import Button from 'components/Button';
import SwitchComponent from 'components/Switch';
import Select from 'components/Select';
import prospectMutation from 'api/mutations/prospectMutation';
import useDatasetById from 'api/hooks/getDatasetById';

interface Props {
  selectedProspect: ProspectOneProps | null | undefined;
  setModal: (value: boolean) => void;
  refetch?: any;
  selectedDataSetId?: number;
}

const ProspectMapOfflineModal: FC<Props> = ({
  selectedProspect,
  setModal,
  refetch,
  selectedDataSetId,
}) => {
  const { t } = useTranslation();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [checkedStatus, setCheckedStatus] = useState<boolean>(
    selectedProspect?.status.toLocaleLowerCase() !== 'not contacted',
  );
  const [rating, setRating] = useState<string>('');
  const { mutate } = prospectMutation({
    id: selectedProspect ? selectedProspect?.id : 0,
  });
  const { data: datasetById } = useDatasetById({
    id: selectedDataSetId,
    preferCachedData: true,
  });

  const filteredProspect = datasetById?.prospects?.find(
    (prospect) => prospect.id === Number(selectedProspect?.id),
  );

  useEffect(() => {
    setCheckedStatus(
      selectedProspect?.status !== 'Not contacted',
    );
  }, [selectedProspect]);

  const selectOptions = [
    { value: 'High potential',
      label: t('highPotential') },
    { value: 'Some potential',
      label: t('somePotential') },
    { value: 'No potential',
      label: t('noPotential') },
  ];

  const onSubmitChange = () => {
    if (selectedProspect?.id) {
      mutate(
        {
          status: checkedStatus ? 'Contacted' : 'Not Contacted',
          rating: rating.length > 0 ? rating : filteredProspect?.rating ?? '',
        },
        {
          onSuccess() {
            refetch();
            setIsEdit(false);
          },
        },
      );
    }
  };

  const editingFields = useMemo(() => (
    <>
      <div
        className={`flex mb-3 ${
          isEdit ? 'justify-between items-center' : ''
        }`}
      >
        <div className="text-secondaryText">{t('status')}: &nbsp;</div>
        {isEdit ? (
          <div className="flex items-center justify-center">
            <SwitchComponent
              className="mt-2"
              checked={checkedStatus}
              onChange={setCheckedStatus}
            />
          </div>
        ) : (
          <div className="text-black font-medium">
            {filteredProspect?.status}
          </div>
        )}
      </div>
      <div className=" mb-6 md:mb-4">
        {isEdit ? (
          <Select
            value={
              filteredProspect?.rating
                ? [
                  {
                    value: filteredProspect?.rating,
                    label: filteredProspect?.rating,
                  },
                ]
                : []
            }
            title={t('rating')}
            options={selectOptions}
            onChange={(el) => setRating(el.value)}
            placeholder={t('selectValue')}
          />
        ) : (
          <div className="flex mb-6 md:mb-4 items-center">
            <div className="text-secondaryText">{t('rating')}: &nbsp;</div>
            <div className="text-black font-medium">
              {filteredProspect?.rating}
            </div>
          </div>
        )}
      </div>
    </>
  ), [isEdit, selectedProspect, selectOptions, filteredProspect]);

  return (
    <div className="fixed z-50 top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.5)] flex items-center justify-center h-screen w-screen">
      <div className="bg-white p-8 md:p-4 rounded-2xl flex w-[500px] flex-col  max-w-96 md:max-w-80 text-center">
        <div
          onClick={() => setModal(false)}
          className="cursor-pointer flex justify-end items-end"
        >
          <img src={cancelSvg} alt="cancel" />
        </div>
        <div className="text-2xl font-bold text-primary mb-3 md:text-xl text-center">
          {selectedProspect?.companyName}
        </div>
        <div className="flex items-center justify-center mb-6 md:mb-4">
          <span className="text-sm text-secondaryText">
            {selectedProspect?.latitude}
          </span>
          <span className="text-primary mx-2">|</span>
          <span className="text-sm text-secondaryText">
            {selectedProspect?.longtitude}
          </span>
        </div>
        <div className="flex mb-3">
          <div className="text-secondaryText">{t('industry')}: &nbsp;</div>
          <div className="text-black font-medium">
            {selectedProspect?.industry}
          </div>
        </div>
        <div className="flex mb-3">
          <div className="text-secondaryText">{t('applications')}: &nbsp;</div>
          <div className="text-black font-medium">
            {selectedProspect?.applications}
          </div>
        </div>
        <div className="flex mb-3">
          <div className="text-secondaryText">{t('contact')}: &nbsp;</div>
          <div className="text-black font-medium">
            {selectedProspect?.contact}
          </div>
        </div>
        <div className="flex mb-3">
          <div className="text-secondaryText">{t('contactNumber')}: &nbsp;</div>
          <div className="text-black font-medium">
            {selectedProspect?.contactNumber}
          </div>
        </div>

        {editingFields}

        <div className="flex justify-center">
          {isEdit ? (
            <Button className="w-[126px]" onClick={onSubmitChange}>
              {t('save')}
            </Button>
          ) : (
            <Button
              className="w-[126px]"
              onClick={() => setIsEdit(true)}
              btnType="secondary"
            >
              {t('edit')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProspectMapOfflineModal;
